/**
 * PROVENANCE-SEMANTICS-1 (s164, D-164) — the ONE question "has the user acted on this cell?"
 *
 * `is_seeded` had drifted into carrying two meanings at once:
 *   1. "this cell's content came from the template" (its real, intended meaning), and
 *   2. "the user has touched this cell" — a meaning it only ever expressed by accident,
 *      because `upsertGenericEntry` stamped `isSeeded:false` on every update, including a
 *      decline. Five readers came to depend on that side effect, so a decline resolved
 *      completion / qualified a row for ERS / kept a row rendering ONLY via meaning 2.
 *
 * Ruled (D-164): `declined` alone carries "the user acted on this cell"; `is_seeded` returns
 * to meaning 1 only, and decline/undo no longer touch it. This predicate is where the two
 * signals are combined, so the coupling is expressed once instead of re-derived at five call
 * sites — and so the writer change stays a one-line edit rather than a five-way behaviour hunt.
 *
 * Migration note: under the OLD writer this is exactly equivalent to the `isSeeded === false`
 * checks it replaced, because `declined === true` always co-occurred with `isSeeded === false`.
 * The only newly-true case — declined AND still seeded — could not exist in data until the
 * writer changed. That is what made the reader migration behaviour-neutral.
 */
export interface EntryProvenance {
  isSeeded?: boolean | null;
  declined?: boolean | null;
}

/**
 * Has the user acted on this cell — by entering data, or by explicitly declining it
 * ("I don't have this")?
 *
 * A declined cell is a user statement even though it carries no value, and a non-seeded cell
 * is one the template did not write. Either is enough. Note this asks nothing about whether a
 * VALUE is present: callers that need "holds real data" must test the value themselves (the
 * aggregate percentage counters deliberately do, which is why they are not routed through here).
 */
export function entryUserActioned(entry: EntryProvenance): boolean {
  return entry.declined === true || entry.isSeeded === false;
}

/**
 * Is this `/generic-entries` write a decline or its undo, rather than a content change?
 *
 * True iff the payload carries decline STATE and no value. The client sends decline/undo with no
 * `value` key at all; the Cat-A carve-out force-nulls value on the allowed presence-metadata
 * write, which still reads as empty here. An ordinary edit that merely CLEARS a field carries no
 * decline state, so it is not a decline-state write and keeps stamping `is_seeded = false`.
 *
 * 🔴 s179 (TWO-REASON-DECLINE-1, D-179.1) — decline state is now TWO fields, so this predicate
 * takes either. Before the reason existed, `declined` as a strict boolean was the whole signal.
 * A planner CHANGING THEIR REASON (`wont_store` → `dont_have`) without re-asserting `declined`
 * sends `{declineReason}` alone: under the old predicate that read as a content change and
 * silently rewrote `is_seeded`, re-opening exactly the D-164 provenance bug this module exists to
 * close. A reason-only write is a decline-state write.
 *
 * `declineReason: null` counts — clearing the reason is decline state, not content.
 *
 * Lives here rather than inline in the route so the rule the writer applies is the same rule the
 * tests assert — a mirrored copy in a test proves only that the copy agrees with itself.
 */
/**
 * s223 — is this `/generic-entries` write a SHOW/HIDE of a row, rather than a content change?
 *
 * Same reasoning as the decline rule above (D-164): hiding a row is a user ACTION, not new content, so it must
 * not rewrite `is_seeded` — which means "this cell's content came from the template". Before this, hiding a row
 * stamped every cell on it as planner-typed: a template suggestion the planner had never touched came back from a
 * hide reading as their own words, and on the Government ID rows it would also have stopped
 * `syncGovIdNamesFromIdentity` re-syncing the name (it re-syncs only an auto-populated, still-seeded value).
 *
 * True iff the payload carries `isHidden` and nothing else that could change content or decline state.
 */
export function isVisibilityOnlyWrite(body: {
  value?: unknown;
  isHidden?: unknown;
  declined?: unknown;
  declineReason?: unknown;
  declineNote?: unknown;
}): boolean {
  return (
    (body.isHidden === true || body.isHidden === false) &&
    body.value === undefined &&
    body.declined === undefined &&
    body.declineReason === undefined &&
    body.declineNote === undefined
  );
}

export function isDeclineStateWrite(body: {
  declined?: unknown;
  declineReason?: unknown;
  declineNote?: unknown;
  value?: unknown;
}): boolean {
  const isStrictBoolean = body.declined === true || body.declined === false;
  // `in`-style presence, not truthiness: `declineReason: null` is a real clear-the-reason write,
  // and `undefined` is the absent case. A string is any reason (validity is the route's 400, not
  // this predicate's question — this asks only "what KIND of write is this?").
  const carriesReason = body.declineReason !== undefined;
  // 🔴 s179 rebuild: the NOTE is decline state too. A planner editing only their location note
  // ("safe deposit box" to "the blue folder") sends `{declineNote}` alone; under a reason-only
  // predicate that read as a content change and rewrote `is_seeded` — the same D-164 trap the
  // reason itself had, one field further along.
  const carriesNote = body.declineNote !== undefined;
  const carriesNoValue = body.value === undefined || String(body.value ?? "").trim() === "";
  return (isStrictBoolean || carriesReason || carriesNote) && carriesNoValue;
}
